import React, { useState, useEffect, useRef } from 'react';
import vendorAuthService from '../../services/vendorAuthService';
import './VendorProfile.css';

const VendorProfile = () => {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);
  
  const [formData, setFormData] = useState({
    phone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    country: '',
    postal_code: ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const data = await vendorAuthService.getProfile();
      setProfile(data);
      setFormData({
        phone: data.phone || '',
        website: data.website || '',
        address: data.address || '',
        city: data.city || '',
        state: data.state || '',
        country: data.country || '',
        postal_code: data.postal_code || ''
      });
      setError(null);
    } catch (err) {
      console.error('Error loading profile:', err);
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await vendorAuthService.updateProfile(formData);
      setProfile(response.vendor);
      setSuccess('Profile updated successfully');
      setIsEditing(false);
      setError(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error updating profile:', err);
      setError(err.response?.data?.error || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    try {
      setLoading(true);
      await vendorAuthService.changePassword(
        passwordData.currentPassword,
        passwordData.newPassword
      );
      setSuccess('Password changed successfully');
      setShowPasswordModal(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
      setError(null);
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error changing password:', err);
      setError(err.response?.data?.error || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      phone: profile.phone || '',
      website: profile.website || '',
      address: profile.address || '',
      city: profile.city || '',
      state: profile.state || '',
      country: profile.country || '',
      postal_code: profile.postal_code || ''
    });
    setIsEditing(false);
    setError(null);
  };

  const handleImageClick = () => {
    fileInputRef.current?.click();
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image size must be less than 5MB');
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('profilePicture', file);

      const response = await vendorAuthService.uploadProfilePicture(formData);
      
      // Update profile with new picture URL
      setProfile(prev => ({
        ...prev,
        profile_picture_url: response.profile_picture_url
      }));
      
      setSuccess('Profile picture updated successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error uploading profile picture:', err);
      setError(err.response?.data?.error || 'Failed to upload profile picture');
    } finally {
      setUploadingImage(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = async () => {
    if (!window.confirm('Are you sure you want to remove your profile picture?')) {
      return;
    }

    try {
      setUploadingImage(true);
      setError(null);
      
      await vendorAuthService.removeProfilePicture();
      
      // Update profile to remove picture URL
      setProfile(prev => ({
        ...prev,
        profile_picture_url: null
      }));
      
      setSuccess('Profile picture removed successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error removing profile picture:', err);
      setError(err.response?.data?.error || 'Failed to remove profile picture');
    } finally {
      setUploadingImage(false);
    }
  };

  if (loading && !profile) {
    return (
      <div className="vendor-profile-container">
        <div className="loading-spinner">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="vendor-profile-container">
      <div className="vendor-profile-header">
        <h1>Vendor Profile</h1>
        <div className="profile-actions">
          {!isEditing ? (
            <>
              <button 
                className="btn btn-primary"
                onClick={() => setIsEditing(true)}
              >
                <i className="fas fa-edit"></i> Edit Profile
              </button>
              <button 
                className="btn btn-secondary"
                onClick={() => setShowPasswordModal(true)}
              >
                <i className="fas fa-key"></i> Change Password
              </button>
            </>
          ) : (
            <>
              <button 
                className="btn btn-success"
                onClick={handleSubmit}
                disabled={loading}
              >
                <i className="fas fa-save"></i> Save Changes
              </button>
              <button 
                className="btn btn-secondary"
                onClick={handleCancel}
                disabled={loading}
              >
                <i className="fas fa-times"></i> Cancel
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <i className="fas fa-exclamation-circle"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <i className="fas fa-check-circle"></i>
          {success}
        </div>
      )}

      <div className="profile-content">
        {/* Profile Picture Section */}
        <div className="profile-section profile-picture-section">
          <h2>Profile Picture</h2>
          <div className="profile-picture-container">
            <div className="profile-picture-wrapper">
              {profile?.profile_picture_url ? (
                <img
                  src={profile.profile_picture_url}
                  alt="Profile"
                  className="profile-picture"
                />
              ) : (
                <div className="profile-picture-placeholder">
                  <i className="fas fa-building"></i>
                </div>
              )}
              {uploadingImage && (
                <div className="profile-picture-overlay">
                  <div className="spinner"></div>
                </div>
              )}
            </div>
            <div className="profile-picture-actions">
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={handleImageChange}
                style={{ display: 'none' }}
              />
              <button
                className="btn btn-primary"
                onClick={handleImageClick}
                disabled={uploadingImage}
              >
                <i className="fas fa-upload"></i>
                {profile?.profile_picture_url ? 'Change Picture' : 'Upload Picture'}
              </button>
              {profile?.profile_picture_url && (
                <button
                  className="btn btn-secondary"
                  onClick={handleRemoveImage}
                  disabled={uploadingImage}
                >
                  <i className="fas fa-trash"></i>
                  Remove Picture
                </button>
              )}
            </div>
            <p className="profile-picture-hint">
              Accepted formats: JPEG, PNG, GIF, WebP (Max 5MB)
            </p>
          </div>
        </div>

        {/* Company Information - Read Only */}
        <div className="profile-section">
          <h2>Company Information</h2>
          <div className="profile-grid">
            <div className="profile-field">
              <label>Company Name</label>
              <div className="field-value">{profile?.obj?.vendor?.company_name || 'N/A'}</div>
            </div>
            <div className="profile-field">
              <label>Vendor Code</label>
              <div className="field-value">{profile?.obj?.vendor?.vendor_code || 'N/A'}</div>
            </div>
            <div className="profile-field">
              <label>Email</label>
              <div className="field-value">{profile?.obj?.vendor?.email || 'N/A'}</div>
            </div>
            <div className="profile-field">
              <label>Status</label>
              <div className="field-value">
                <span className={`status-badge status-${profile?.obj?.vendor?.status?.toLowerCase()}`}>
                  {profile?.obj?.vendor?.status.toLowerCase()|| 'N/A'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Contact Information - Editable */}
        <div className="profile-section">
          <h2>Contact Information</h2>
          <form onSubmit={handleSubmit}>
            <div className="profile-grid">
              <div className="profile-field">
                <label htmlFor="phone">Phone</label>
                {isEditing ? (
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="Enter phone number"
                  />
                ) : (
                  <div className="field-value">{profile?.obj.phone || 'Not provided'}</div>
                )}
              </div>
              <div className="profile-field">
                <label htmlFor="website">Website</label>
                {isEditing ? (
                  <input
                    type="url"
                    id="website"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                ) : (
                  <div className="field-value">
                    {profile?.website ? (
                      <a href={profile.website} target="_blank" rel="noopener noreferrer">
                        {profile.website}
                      </a>
                    ) : (
                      'Not provided'
                    )}
                  </div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Address Information - Editable */}
        <div className="profile-section">
          <h2>Address Information</h2>
          <form onSubmit={handleSubmit}>
            <div className="profile-grid">
              <div className="profile-field full-width">
                <label htmlFor="address">Street Address</label>
                {isEditing ? (
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    placeholder="Enter street address"
                    rows="2"
                  />
                ) : (
                  <div className="field-value">{profile?.address || 'Not provided'}</div>
                )}
              </div>
              <div className="profile-field">
                <label htmlFor="city">City</label>
                {isEditing ? (
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    placeholder="Enter city"
                  />
                ) : (
                  <div className="field-value">{profile?.city || 'Not provided'}</div>
                )}
              </div>
              <div className="profile-field">
                <label htmlFor="state">State/Province</label>
                {isEditing ? (
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleInputChange}
                    placeholder="Enter state"
                  />
                ) : (
                  <div className="field-value">{profile?.state || 'Not provided'}</div>
                )}
              </div>
              <div className="profile-field">
                <label htmlFor="country">Country</label>
                {isEditing ? (
                  <input
                    type="text"
                    id="country"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                    placeholder="Enter country"
                  />
                ) : (
                  <div className="field-value">{profile?.country || 'Not provided'}</div>
                )}
              </div>
              <div className="profile-field">
                <label htmlFor="postal_code">Postal Code</label>
                {isEditing ? (
                  <input
                    type="text"
                    id="postal_code"
                    name="postal_code"
                    value={formData.postal_code}
                    onChange={handleInputChange}
                    placeholder="Enter postal code"
                  />
                ) : (
                  <div className="field-value">{profile?.postal_code || 'Not provided'}</div>
                )}
              </div>
            </div>
          </form>
        </div>

        {/* Organization Information - Read Only */}
        {profile?.organization && (
          <div className="profile-section">
            <h2>Organization</h2>
            <div className="profile-grid">
              <div className="profile-field">
                <label>Organization Name</label>
                <div className="field-value">{profile.organization.name || 'N/A'}</div>
              </div>
              <div className="profile-field">
                <label>Organization Code</label>
                <div className="field-value">{profile.organization.code || 'N/A'}</div>
              </div>
            </div>
             <div className="profile-field mt-5">
                <label>Address</label>
                <div className="field-value">{profile.organization.address || 'N/A'}, {profile.organization.city || 'N/A'}, {profile.organization.state || 'N/A'}, {profile.organization.country || 'N/A'}

                </div>
              </div>
          </div>
        )}
      </div>

      {/* Change Password Modal */}
      {showPasswordModal && (
        <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Change Password</h2>
              <button 
                className="modal-close"
                onClick={() => setShowPasswordModal(false)}
              >
                <i className="fas fa-times"></i>
              </button>
            </div>
            <form onSubmit={handlePasswordSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <input
                    type="password"
                    id="currentPassword"
                    name="currentPassword"
                    value={passwordData.currentPassword}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Enter current password"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    name="newPassword"
                    value={passwordData.newPassword}
                    onChange={handlePasswordChange}
                    required
                    minLength="8"
                    placeholder="Enter new password (min 8 characters)"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={passwordData.confirmPassword}
                    onChange={handlePasswordChange}
                    required
                    placeholder="Confirm new password"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowPasswordModal(false)}
                  disabled={loading}
                >
                  Cancel
                </button>
                <button 
                  type="submit"
                  className="btn btn-primary"
                  disabled={loading}
                >
                  {loading ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorProfile;

